import { makeStyles, Paper, Tab, Tabs, Theme } from '@material-ui/core';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import DateRange from '@material-ui/icons/DateRange';
import NotificationImportant from '@material-ui/icons/NotificationImportant';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { ReactComponent as BaseSvg } from '../../../../assets/base.svg';
import { ReactComponent as FeedSvg } from '../../../../assets/feed.svg';
import {
  GetNotificationActivitySubscription,
  GetNotificationActivitySubscriptionVariables,
  GetNotificationsActivityUnreadCountQuery,
  useGetNotificationsActivityUnreadCountQuery,
} from '../../../../graphql/gen/types';
import { GET_NOTIFICATION_ACTIVITY_SUBSCRIPTION } from '../../../../graphql/subscriptions/GetNotificationActivity';
import { colorPalette, TextSizesEnum } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { NotificationBadge } from '../NavBar/NotificationBadge';
import { PlexFeedTab, useSelectedFeedTab } from './useSelectedFeedTab';

export function PlexFeedTabsHeader(): React.ReactElement {
  const [selectedTab, setSelectedTab] = useSelectedFeedTab();
  const classes = useStyles();
  const { data, subscribeToMore } = useGetNotificationsActivityUnreadCountQuery();
  const notificationsCount = data?.loggedIn?.notificationsActivityUnreadCount || 0;

  useEffect(() => {
    const unsubscribe = subscribeToMore<
      GetNotificationActivitySubscription,
      GetNotificationActivitySubscriptionVariables
    >({
      document: GET_NOTIFICATION_ACTIVITY_SUBSCRIPTION,
      variables: {},
      updateQuery: (
        prev: GetNotificationsActivityUnreadCountQuery,
        { subscriptionData }: { subscriptionData: { data: GetNotificationActivitySubscription } }
      ) => {
        return {
          ...prev,
          loggedIn: {
            ...prev.loggedIn,
            notificationsActivityUnreadCount: subscriptionData.data.notificationActivity.totalUnreadCount,
          },
        };
      },
    });

    return unsubscribe;
  }, [subscribeToMore]);

  const handleTabChange = (event: React.ChangeEvent<unknown>, newValue: PlexFeedTab): void => {
    setSelectedTab(newValue);
  };
  const tabs: Array<{ value: PlexFeedTab; text: string | null; icon: React.ReactElement }> = [
    {
      value: 'feed',
      text: 'Feed',
      icon: <FeedSvg />,
    },
    {
      value: 'tasks',
      text: 'Tasks',
      icon: <AssignmentTurnedInIcon />,
    },
    {
      value: 'timeline',
      text: 'Timeline',
      icon: <DateRange />,
    },
    {
      value: 'base',
      text: 'Base',
      icon: <BaseSvg />,
    },
    {
      value: 'notifications',
      text: null,
      icon: (
        <div className={classes.notificationContainer}>
          <NotificationImportant />
          {!!notificationsCount && <NotificationBadge count={notificationsCount} />}
        </div>
      ),
    },
  ];

  return (
    <Paper square classes={{ root: classes.muiPaperRoot }}>
      <Tabs
        classes={{
          flexContainer: classes.muiTabsFlexContainer,
          indicator: `${classes.muiTabsIndicator} ${selectedTab ? '' : classes.hidden}`,
        }}
        value={selectedTab || false}
        onChange={handleTabChange}
        indicatorColor="primary"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            classes={{
              selected: classNames(classes.muiTabSelected, tab.value === 'feed' && classes.muiTabSelectedFeed),
              textColorInherit: classNames(
                classes.muiTabTextColorInherit,
                tab.value === 'feed' && classes.muiTabTextColorInheritFeed
              ),
            }}
            value={tab.value}
            label={
              <div className={classes.tabContainer}>
                {tab.icon}
                {tab.text != null && (
                  <PlexTypography className={classes.tabText} text={tab.text} size={TextSizesEnum.XLarge} />
                )}
              </div>
            }
          />
        ))}
      </Tabs>
    </Paper>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  muiPaperRoot: {
    display: 'flex',
    boxShadow: 'none',
    background: 'transparent',
  },
  muiTabsFlexContainer: {
    height: 80,
  },
  muiTabsIndicator: {
    width: '103px !important',
    height: 3,
    backgroundColor: colorPalette.primaryColorBlue._500,
    borderRadius: '4px 4px 0px 0px',
    marginLeft: 32,
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabText: {
    fontWeight: 'bold',
    marginLeft: 12,
  },
  muiTabSelected: {
    '& path': {
      fill: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
    },
    color: `${colorPalette.primaryColorBlue._500} !important`, // muiTabTextColorInherit will overwrite this otherwise
    fill: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
  },
  muiTabTextColorInherit: {
    '& path': {
      fill: colorPalette.purpleGray._500, // sets the svg color
    },
    color: colorPalette.purpleGray._500,
    fill: colorPalette.purpleGray._500, // sets the svg color
    opacity: 1,
  },
  // we also have to set stroke color for the feed icon
  muiTabSelectedFeed: {
    '& path': {
      stroke: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
    },
    stroke: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
  },
  muiTabTextColorInheritFeed: {
    '& path': {
      stroke: colorPalette.purpleGray._500, // sets the svg color
    },
    stroke: colorPalette.purpleGray._500, // sets the svg color
  },
  hidden: {
    display: 'none',
  },
  notificationContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));
